import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { BoardTaskView } from "@dayvise/client-sdk";
import { useDayviseClient } from "./providers.tsx";
import { Badge, Button, Card, MUTED_TEXT, Spinner, Switch, TextField, rowStyle, stackStyle } from "@dayvise/react-ui";
import { useBoard, useBoardMutations, useBoards } from "./hooks.ts";

const STATUS_TONE: Record<string, "neutral" | "accent" | "warning" | "danger"> = {
  backlog: "neutral",
  ready: "accent",
  in_progress: "accent",
  blocked: "warning",
  review: "warning",
  done: "neutral",
  cancelled: "neutral",
};

/**
 * The board surface: durable columns and tasks, what is actually ready, and the
 * one action that starts an agent run. Dispatch remembers the task's revision, so
 * pressing it twice continues the same run instead of starting a second one.
 */
export function BoardsSection() {
  const [includeArchived, setIncludeArchived] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [newBoard, setNewBoard] = useState("");
  const [newColumn, setNewColumn] = useState("");
  const [newTask, setNewTask] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const boards = useBoards(includeArchived);
  const list = boards.data?.boards ?? [];
  const activeCase = selectedId ?? list[0]?.boardId ?? null;
  const board = useBoard(activeCase);
  const mutations = useBoardMutations();
  const view = board.data?.view;
  const busy = mutations.addColumn.isPending || mutations.addTask.isPending || mutations.dispatch.isPending || mutations.cancelTask.isPending;
  const error = boards.error ?? board.error ?? mutations.create.error ?? mutations.dispatch.error ?? mutations.cancelTask.error ?? mutations.updateTask.error;

  const dispatch = (entry: BoardTaskView) => {
    if (activeCase === null) return;
    setNotice(null);
    mutations.dispatch.mutate({ boardId: activeCase, taskId: entry.task.taskId }, {
      onSuccess: (result) => setNotice(result.runId === null
        ? `\`${entry.task.title}\` is already claimed by an agent run; nothing new was started.`
        : `Dispatched \`${entry.task.title}\` as run ${result.runId}. The run finishing moves it to review.`),
    });
  };

  return (
    <Card
      title="Boards"
      subtitle="Boards are private to their owner until the owner shares them with the workspace. A task runs on its own branch, and only the board owner marks it done."
    >
      <div style={stackStyle("var(--dayvise-space-sm)")}>
        <div style={rowStyle("var(--dayvise-space-xs)")}>
          <TextField label="New board name" value={newBoard} onChange={(event) => setNewBoard(event.target.value)} />
          <Button
            size="sm"
            variant="secondary"
            disabled={newBoard.trim().length === 0 || mutations.create.isPending}
            onClick={() => {
              setNotice(null);
              mutations.create.mutate({ name: newBoard.trim() }, {
                onSuccess: (result) => { setNewBoard(""); setSelectedId(result.board.boardId); },
              });
            }}
          >
            Create private board
          </Button>
          <Switch label="Show archived boards" checked={includeArchived} onChange={setIncludeArchived} />
          <Button size="sm" variant="ghost" loading={boards.isFetching} onClick={() => void boards.refetch()}>Reload</Button>
        </div>
        {error ? <p role="alert" style={{ color: "var(--dayvise-color-danger)" }}>{error instanceof Error ? error.message : "The board request failed"}</p> : null}
        {notice !== null ? <p role="status" style={MUTED_TEXT}>{notice}</p> : null}
        {boards.isPending ? <Spinner label="Loading boards" /> : null}
        {!boards.isPending && list.length === 0 ? <p style={{ ...MUTED_TEXT, margin: 0 }}>No boards yet. Create one, add a column, then a task.</p> : null}
        {list.length > 0 ? (
          <div style={rowStyle("var(--dayvise-space-xs)")}>
            {list.map((entry) => (
              <Button
                key={entry.boardId}
                size="sm"
                variant={entry.boardId === activeCase ? "primary" : "ghost"}
                onClick={() => { setSelectedId(entry.boardId); setNotice(null); }}
              >
                {entry.name}{entry.archivedAt === null ? "" : " (archived)"}
              </Button>
            ))}
          </div>
        ) : null}

        {activeCase !== null && view !== undefined ? (
          <>
            <div style={rowStyle("var(--dayvise-space-xs)")}>
              <Badge tone={view.board.visibility === "workspace" ? "accent" : "neutral"}>{view.board.visibility}</Badge>
              <span style={MUTED_TEXT}>
                {view.counts.active} active · {view.counts.ready} ready · {view.columns.length} column(s)
                {view.dispatches.filter((entry) => entry.dispatch.state === "dispatched" || entry.dispatch.state === "running").length > 0
                  ? ` · ${view.dispatches.filter((entry) => entry.dispatch.state === "dispatched" || entry.dispatch.state === "running").length} running`
                  : ""}
              </span>
              <Button size="sm" variant="ghost" loading={mutations.settleDispatches.isPending} onClick={() => mutations.settleDispatches.mutate(activeCase)}>Settle runs</Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={mutations.archive.isPending}
                onClick={() => mutations.archive.mutate({ boardId: activeCase, restore: view.board.archivedAt !== null })}
              >
                {view.board.archivedAt === null ? "Archive board" : "Restore board"}
              </Button>
            </div>

            <div style={rowStyle("var(--dayvise-space-xs)")}>
              <TextField label="New column" value={newColumn} onChange={(event) => setNewColumn(event.target.value)} />
              <Button
                size="sm"
                variant="secondary"
                disabled={newColumn.trim().length === 0 || busy}
                onClick={() => mutations.addColumn.mutate({ boardId: activeCase, name: newColumn.trim() }, { onSuccess: () => setNewColumn("") })}
              >
                Add column
              </Button>
              <TextField label="New task" value={newTask} onChange={(event) => setNewTask(event.target.value)} />
              <Button
                size="sm"
                variant="secondary"
                disabled={newTask.trim().length === 0 || view.columns.length === 0 || busy}
                onClick={() => mutations.addTask.mutate({ boardId: activeCase, title: newTask.trim() }, { onSuccess: () => setNewTask("") })}
              >
                Add task
              </Button>
            </div>

            {view.columns.map((column) => {
              const tasks = view.tasks.filter((entry) => entry.task.columnId === column.columnId);
              return (
                <Card key={column.columnId} title={column.name} subtitle={`${tasks.length} task(s)${column.wipLimit === null ? "" : ` · work-in-progress limit ${column.wipLimit}`}`}>
                  <div style={stackStyle("var(--dayvise-space-xs)")}>
                    {tasks.length === 0 ? <span style={MUTED_TEXT}>No tasks in this column.</span> : null}
                    {tasks.map((entry) => {
                      const live = view.dispatches.find((candidate) => candidate.dispatch.taskId === entry.task.taskId && (candidate.dispatch.state === "dispatched" || candidate.dispatch.state === "running"));
                      const canDispatch = entry.readiness.ready && entry.task.assigneeKind === "agent";
                      return (
                        <div key={entry.task.taskId} style={{ ...stackStyle("var(--dayvise-space-xxs)"), borderTop: "1px solid var(--dayvise-color-border)", paddingTop: "var(--dayvise-space-xs)" }}>
                          <div style={rowStyle("var(--dayvise-space-xs)")}>
                            <strong>{entry.task.title}</strong>
                            <Badge tone={STATUS_TONE[entry.task.status] ?? "neutral"}>{entry.task.status}</Badge>
                            {entry.task.assigneeKind === "agent" ? <Badge tone="neutral">agent {entry.task.assigneeAgentId}</Badge> : null}
                            {live ? <Badge tone="accent">{live.dispatch.state}{live.run === null ? "" : ` · run ${live.run.state}`}{live.dispatch.runId === null ? "" : ` · ${live.dispatch.runId}`}</Badge> : null}
                          </div>
                          <span style={MUTED_TEXT}>
                            {entry.readiness.ready ? "Ready." : `Blocked: ${entry.readiness.reasons[0] ?? "a dependency is unfinished"}`}
                            {` · ${entry.messageCount} message(s) · ${entry.artifactCount} artifact(s) · revision ${entry.task.revision}`}
                            {entry.openQuestions > 0 ? ` · waiting on ${entry.openQuestions} answer(s) from another task` : ""}
                            {entry.pendingInput > 0 ? ` · needs your input (${entry.pendingInput})` : ""}
                          </span>
                          {live?.run ? (
                            <span style={MUTED_TEXT}>
                              {`run usage: ${live.run.usage.inputTokens + live.run.usage.outputTokens} token(s) · ${live.run.usage.toolCalls} tool call(s)${live.run.pauseReason === null ? "" : ` · waiting: ${live.run.pauseReason}`}`}
                            </span>
                          ) : null}
          {live?.dispatch.branchId && view.board.conversationId ? (
            <DispatchWorktreeProvenance conversationId={view.board.conversationId} branchId={live.dispatch.branchId} />
          ) : null}
                          <div style={rowStyle("var(--dayvise-space-xs)")}>
                            <Button
                              size="sm"
                              variant="primary"
                              disabled={!canDispatch || busy}
                              onClick={() => dispatch(entry)}
                              title={canDispatch ? undefined : "Assign an agent and finish its dependencies first"}
                            >
                              Dispatch
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={live === undefined || busy}
                              onClick={() => mutations.cancelTask.mutate({ boardId: activeCase, taskId: entry.task.taskId, reason: "cancelled from the board" })}
                            >
                              Cancel run
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={!entry.readiness.ready || entry.task.status === "done" || busy}
                              onClick={() => {
                                if (entry.task.completionCriteria !== null && entry.task.completionCriteria !== undefined
                                  && !confirm(`Confirm the completion criteria are met:\n\n${entry.task.completionCriteria}`)) return;
                                mutations.updateTask.mutate({ boardId: activeCase, taskId: entry.task.taskId, status: "done", criteriaMet: true, expectedRevision: entry.task.revision });
                              }}
                            >
                              Mark done
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              );
            })}
          </>
        ) : null}
      </div>
    </Card>
  );
}

/**
 * GIT-10 kanban provenance: the worktrees a dispatched task's run actually
 * created, read from the same durable transcript-derived record the worktree
 * UI uses — the assignment (agent, run, branch) connects to its real worktree.
 */
function DispatchWorktreeProvenance({ conversationId, branchId }: { conversationId: string; branchId: string }) {
  const client = useDayviseClient();
  const worktrees = useQuery({
    queryKey: ["worktrees", conversationId, branchId] as const,
    queryFn: () => client.worktrees.list(conversationId, { branchId }),
  });
  const rows = (worktrees.data?.worktrees ?? []).filter((row) => row.state !== "removed" && row.state !== "pruned");
  if (rows.length === 0) return null;
  return (
    <span style={MUTED_TEXT}>
      {"worktree: "}
      {rows.map((row) => `${row.name} (${row.branch}${row.head ? ` @ ${row.head.slice(0, 10)}` : ""})`).join(", ")}
    </span>
  );
}
